import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
  useContext,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useApolloClient, useQuery } from '@apollo/client';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import c from 'classnames';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { match, P } from 'ts-pattern';
import {
  QueryResourceWhatsappArgs,
  WhatsappTemplate,
  Query,
} from '@lgg/isomorphic/types/__generated__/graphql';
import {
  LggDropdownButtonWithoutOverlay,
  LggSelectableOptionsDropdownButtonWithCustomTrigger,
} from 'src/components/general/button/dropdown-button';
import { Tooltip } from 'src/components/general/display/tooltip';
import { SelectableOptionsDrawer } from 'src/components/general/drawer/bottom/selectable-options-bottom-drawer';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { ListLoadingItem } from 'src/components/general/feedback/list-loading-item';
import { Icon } from 'src/components/general/icon';
import { TemplatePickerModal } from 'src/components/general/modals/template-picker-modal';
import { Center } from 'src/components/layout/center';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { LoadingIndicator } from 'src/components/loading-fallback';
import { useWhatsappTemplateQuery } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/channels/whatsapp/whatsapp-template/queries';
import { WhatsappTemplateListItem } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/channels/whatsapp/whatsapp-template/whatsapp-template-list-item';
import { ContactInteractionInputManagerContext } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interaction-input-manager';
import { CONTACT_INTERACTION_CHANNEL_AVAILABILITY_FOR_CONTACT } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/graphql-operations';
import {
  conversationDropdownButtonStyles,
  ConversationTextFilterInput,
} from 'src/components/pages/conversations/components/filters/shared';
import { RESET_EDITOR_COMMAND } from 'src/components/pages/conversations/components/general/lexical-editor/plugins/custom-clear-editor-plugin';
import {
  ManageWhatsappTemplatesButton,
  WhatsappSyncButton,
} from 'src/components/pages/whatsapp-channel/components/shared';
import { getManageTemplatesUrl } from 'src/components/pages/whatsapp-channel/shared/helpers';
import { useHandleSyncWhatsappTemplates } from 'src/components/pages/whatsapp-channel/shared/hooks';
import { RESOURCE_WHATSAPP_QUERY } from 'src/components/pages/whatsapp-channel/shared/queries';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useDebouncedState } from 'src/hooks/use-debounced-state';
import { useVisible } from 'src/hooks/use-visible';

const LanguageDropdownButton = styled(LggDropdownButtonWithoutOverlay)`
  ${conversationDropdownButtonStyles};
  border: 1px solid rgba(152, 169, 188, 0.25);
  min-width: 99px;
  padding-right: 10px;

  span[role='img'] {
    margin-left: 5px;
  }

  ${up('md')} {
    padding-right: 15px;
    min-width: 160px;

    span[role='img'] {
      margin-left: 10px;
    }
  }
`;

const TemplateSearchInput = styled(ConversationTextFilterInput)`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.koala};
  font-size: 14px;
  line-height: 17px;
  background-color: white;

  .lgg-icon {
    svg path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }

  ${up('md')} {
    input {
      padding: 11px 10px;
    }
  }
`;

const FilterLabel = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 11px;
  line-height: 13px;
  margin-bottom: 1px;
  text-transform: capitalize;
`;

const SearchInputContainer = styled(FlexColumn)`
  margin-left: 15px;
`;

const TriggerIcon = styled(Icon)`
  svg path {
    fill: ${({ theme }) => theme.colors.flint};
  }

  &.active,
  &:hover {
    svg path {
      fill: ${({ theme }) => theme.colors.smalt};
    }
  }
`;

const baseTemplateListStyles = css`
  height: 100%;
  background-color: ${({ theme }) => theme.colors.white};

  ${up('md')} {
    height: 420px;
    border-radius: 4px;
  }
`;

const LoadingContainer = styled(FlexRow)`
  ${baseTemplateListStyles};
  display: flex;
  align-items: center;
  justify-content: center;
`;

type SendTemplateOptionIconProps = {
  showModal: () => void;
  visible: boolean;
};

export const SendTemplateOptionIcon = memo<SendTemplateOptionIconProps>(
  ({ showModal, visible }) => {
    const { t } = useTranslation(['conversations']);
    const tooltipVisibilityHandler = useVisible();
    const breakpointUpMd = useBreakpoint(up('md'));

    useEffect(() => {
      if (visible && tooltipVisibilityHandler.visible) {
        tooltipVisibilityHandler.close();
      }
    }, [tooltipVisibilityHandler, visible]);

    return (
      <Tooltip
        title={t(
          'conversations:messageInput.modes.reply.messages.whatsapp.whatsappTemplate.templateOptionTooltip',
        )}
        visible={breakpointUpMd && tooltipVisibilityHandler.visible}
      >
        <span
          onMouseEnter={tooltipVisibilityHandler.show}
          onMouseLeave={tooltipVisibilityHandler.close}
        >
          <Center>
            <TriggerIcon
              type="whatsappTemplates"
              lggTestId="input-area-whatsapp-template-picker-trigger"
              onClick={showModal}
              className={c({ active: visible })}
            />
          </Center>
        </span>
      </Tooltip>
    );
  },
);

const StyledEmptyState = styled(FlexColumn)`
  ${baseTemplateListStyles};
  align-items: center;
  padding: 70px 30px 0;

  ${up('md')} {
    padding: 93px 170px 0;
  }

  .iconContainer {
    align-items: center;
    background-color: ${({ theme }) => theme.colors.porcelain};
    border-radius: 50%;
    display: flex;
    height: 90px;
    justify-content: center;
    width: 90px;

    svg {
      height: 30px;
      width: 30px;

      path {
        fill: ${({ theme }) => theme.colors.gogo};
      }
    }
  }

  .title {
    color: ${({ theme }) => theme.colors.smalt};
    font-family: ${({ theme }) => theme.font.regular};
    font-size: 18px;
    font-stretch: normal;
    font-style: normal;
    font-weight: normal;
    letter-spacing: -0.36px;
    line-height: 22px;
    margin: 20px 0 10px;
    text-align: center;
  }

  .message {
    color: ${({ theme }) => theme.colors.flint};
    font-family: ${({ theme }) => theme.font.regular};
    font-size: 14px;
    font-stretch: normal;
    font-style: normal;
    font-weight: normal;
    letter-spacing: normal;
    line-height: 18px;
    text-align: center;

    a,
    a:visited,
    a:active,
    a:hover {
      color: ${({ theme }) => theme.colors.gogo};
      font-family: ${({ theme }) => theme.font.medium};
    }
  }
`;

const TemplateListFooter = styled.div`
  height: 30px;
`;

const EmptyState = memo<{ manageTemplatesUrl: string }>(({ manageTemplatesUrl }) => {
  const { t } = useTranslation(['conversations']);

  return (
    <StyledEmptyState data-lgg-id="templates-empty-state">
      <div className="iconContainer">
        <Icon type="whatsappTemplates" />
      </div>
      <span className="title">
        {t('conversations:messageInput.options.whatsapp.templatePicker.emptyState.title')}
      </span>
      <span className="message">
        <Trans
          i18nKey="conversations:messageInput.options.whatsapp.templatePicker.emptyState.message"
          components={{
            metaLink: (
              <Link
                data-lgg-id="manage-template-meta-link"
                to={{ pathname: manageTemplatesUrl }}
                target="_blank"
              />
            ),
          }}
        />
      </span>
    </StyledEmptyState>
  );
});

type AddWhatsappTemplateOptionProps = {
  resourceData: {
    resourceId: string;
  };
  renderTrigger: (props: { showModal; visible }) => ReactNode;
  onTemplateSelect: (template: WhatsappTemplate) => void;
};

export const AddWhatsappTemplateOption = ({
  resourceData: { resourceId },
  renderTrigger,
  onTemplateSelect,
}: AddWhatsappTemplateOptionProps) => {
  const { t } = useTranslation(['conversations', 'common', 'errors']);
  const visibilityHandler = useVisible();
  const languageVisibilityHandler = useVisible();
  const breakpointUpMd = useBreakpoint(up('md'));
  const [keyword, setKeyword] = useState('');
  const [language, setLanguage] = useState('all');
  const debouncedKeyword = useDebouncedState(keyword);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsappTemplate | undefined>(
    undefined,
  );
  const showNotification = useShowNotification();
  const [editor] = useLexicalComposerContext();
  const { clearMessageInputStates } = useContext(ContactInteractionInputManagerContext);

  useEffect(() => {
    if (!visibilityHandler.visible) {
      setSelectedTemplate(undefined);
    }
  }, [visibilityHandler.visible]);

  const {
    handleLoadBottom,
    firstItemIndex,
    loadingMoreBottom,
    nodes,
    data,
    refetch,
    loading: loadingTemplates,
  } = useWhatsappTemplateQuery({
    resourceId,
    keyword: debouncedKeyword,
    language,
    skip: !visibilityHandler.visible,
  });

  const { data: resourceWhatsappData, loading: loadingResourceWhatsappData } = useQuery<
    Pick<Query, 'resourceWhatsapp'>,
    QueryResourceWhatsappArgs
  >(RESOURCE_WHATSAPP_QUERY, {
    variables: {
      id: Number(resourceId),
    },
  });

  const apolloClient = useApolloClient();

  const handleUpdateWhatsappTemplatesSyncError = useCallback(
    (message: string) => {
      showNotification({
        title: t('errors:notification.title'),
        message: t('conversations:messageInput.options.whatsapp.templatePicker.error', {
          error: message,
        }),
        type: 'error',
      });

      void apolloClient.refetchQueries({
        include: [CONTACT_INTERACTION_CHANNEL_AVAILABILITY_FOR_CONTACT],
      });

      visibilityHandler.close();
    },
    [apolloClient, showNotification, t, visibilityHandler],
  );

  const { handleSyncTemplates, isSyncingTemplates } = useHandleSyncWhatsappTemplates({
    resourceId: Number(resourceId),
    onCompleted: async (data) => {
      if (!data.updateWhatsappTemplatesSync) {
        return;
      }

      await refetch();
      showNotification({
        title: t(
          'conversations:messageInput.options.whatsapp.templatePicker.templatesSynced',
        ),
        type: 'success',
      });
    },
    onUpdateError: handleUpdateWhatsappTemplatesSyncError,
  });

  const templateCount = useMemo(
    () => data?.whatsappTemplates.totalCount,
    [data?.whatsappTemplates.totalCount],
  );

  const templateSearchInput = useMemo(
    () => (
      <TemplateSearchInput
        data-lgg-id="conversation-keyword-filter-input"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        placeholder={t(
          'conversations:messageInput.options.whatsapp.templatePicker.searchTemplate',
        )}
        leadingIcon={breakpointUpMd ? undefined : 'magnifyingGlass'}
      />
    ),
    [keyword, breakpointUpMd, t],
  );

  const languageDropdownPicker = useMemo(() => {
    const options = [
      {
        label: breakpointUpMd ? t('common:all') : t('common:languages.allLanguages'),
        buttonLabel: !breakpointUpMd && t('common:languages.title'),
        value: 'all',
        'data-lgg-id': 'whatsapp-template-language-dropdown-all-option',
      },
      {
        label: t('common:languages.english'),
        value: 'en',
        'data-lgg-id': 'whatsapp-template-language-dropdown-en-option',
      },
      {
        label: t('common:languages.spanish'),
        value: 'es',
        'data-lgg-id': 'whatsapp-template-language-dropdown-es-option',
      },
    ].map((option) => ({
      ...option,
      onClick: () => {
        setLanguage(option.value);
        languageVisibilityHandler.close();
      },
    }));

    const selectedOption = options.find((option) => option.value === language);

    const dropdownButton = (
      <LanguageDropdownButton
        variant="defaultWhite"
        size="small"
        data-lgg-id="whatsapp-template-language-dropdown-button"
        isActive={languageVisibilityHandler.visible}
        onClick={() =>
          languageVisibilityHandler.setVisible(!languageVisibilityHandler.visible)
        }
      >
        {selectedOption?.buttonLabel || selectedOption?.label}
      </LanguageDropdownButton>
    );

    return breakpointUpMd ? (
      <LggSelectableOptionsDropdownButtonWithCustomTrigger
        options={options}
        children={<span>{dropdownButton}</span>}
        selectedValue={language}
        size="small"
        variant="default"
        visibilityHandler={languageVisibilityHandler}
      />
    ) : (
      <>
        {dropdownButton}
        <SelectableOptionsDrawer
          visible={languageVisibilityHandler.visible}
          title={t('common:languages.mobileTitle')}
          onClose={languageVisibilityHandler.close}
          options={options}
          selectedValue={language}
        />
      </>
    );
  }, [breakpointUpMd, language, languageVisibilityHandler, t]);

  const manageTemplatesUrl = resourceWhatsappData
    ? getManageTemplatesUrl(resourceWhatsappData.resourceWhatsapp.waba)
    : '';

  const whatsappTemplateList = useMemo(() => {
    return match({ loadingTemplates, nodes })
      .with({ loadingTemplates: true, nodes: [] }, () => (
        <LoadingContainer>
          <LoadingIndicator />
        </LoadingContainer>
      ))
      .with({ nodes: [], loadingTemplates: false }, () => (
        <EmptyState manageTemplatesUrl={manageTemplatesUrl} />
      ))
      .with({ nodes: P.when((nodes) => nodes.length > 0) }, ({ nodes }) => (
        <Virtuoso
          ref={virtuosoRef}
          data-lgg-id="whatsapp-template-list"
          data={nodes}
          initialItemCount={0}
          initialTopMostItemIndex={0}
          endReached={async () => {
            await handleLoadBottom();
          }}
          firstItemIndex={firstItemIndex}
          components={{
            Footer: () =>
              loadingMoreBottom ? (
                <ListLoadingItem visible={loadingMoreBottom} />
              ) : (
                <TemplateListFooter />
              ),
          }}
          itemContent={(_, template) => (
            <WhatsappTemplateListItem
              key={template.id}
              template={template}
              selected={!!selectedTemplate?.id && selectedTemplate.id === template.id}
              onClick={() => {
                setSelectedTemplate(template);
              }}
            />
          )}
        />
      ))
      .run();
  }, [
    manageTemplatesUrl,
    firstItemIndex,
    handleLoadBottom,
    loadingMoreBottom,
    loadingTemplates,
    nodes,
    selectedTemplate?.id,
  ]);

  const syncTemplatesButton = useMemo(() => {
    return (
      <WhatsappSyncButton
        testId="whatsapp-template-sync-button"
        isSyncing={isSyncingTemplates}
        onClick={() => {
          void handleSyncTemplates();
        }}
      />
    );
  }, [handleSyncTemplates, isSyncingTemplates]);

  const manageTemplatesButton = useMemo(() => {
    return (
      <ManageWhatsappTemplatesButton
        isLoading={loadingResourceWhatsappData}
        wabaId={resourceWhatsappData?.resourceWhatsapp.waba}
      />
    );
  }, [loadingResourceWhatsappData, resourceWhatsappData?.resourceWhatsapp.waba]);

  return (
    <>
      {renderTrigger({
        visible: visibilityHandler.visible,
        showModal: visibilityHandler.show,
      })}
      {visibilityHandler.visible && (
        <TemplatePickerModal<WhatsappTemplate>
          testId="whatsapp-template-picker-modal"
          pickerTitle={t(
            'conversations:messageInput.options.whatsapp.templatePicker.title',
          )}
          headerExtraContent={
            <>
              {syncTemplatesButton}
              {manageTemplatesButton}
            </>
          }
          selectedTemplate={selectedTemplate}
          onSubmit={() => {
            if (!selectedTemplate) {
              return;
            }

            onTemplateSelect(selectedTemplate);

            if (editor) {
              editor.dispatchCommand(RESET_EDITOR_COMMAND, clearMessageInputStates);
            }

            visibilityHandler.close();
          }}
          itemsCount={templateCount ?? 0}
          filtersContent={
            breakpointUpMd ? (
              <>
                <FlexColumn>
                  <FilterLabel>{t('common:language')}</FilterLabel>
                  {languageDropdownPicker}
                </FlexColumn>
                <SearchInputContainer>
                  <FilterLabel>{t('common:search')}</FilterLabel>
                  {templateSearchInput}
                </SearchInputContainer>
              </>
            ) : (
              <>
                {syncTemplatesButton}
                {languageDropdownPicker}
              </>
            )
          }
          visibilityHandler={visibilityHandler}
          listContent={whatsappTemplateList}
          headerBottomContent={templateSearchInput}
        />
      )}
    </>
  );
};
